import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Skeleton
} from "@mui/material";
import { WSIcons } from "../../../commonFunctions/iIcons";

interface OrderSummaryProps {
  orders: any[];
  handleDelete: (productId: string) => void;
  loading: boolean;
  setTotalAmount: React.Dispatch<React.SetStateAction<number>>;
  weight: number;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  orders,
  handleDelete,
  setTotalAmount,
  loading,
  weight
}) => {
  const calculateTotal = (): number => {
    const seenWeightBasedServices = new Set<string>();
  
    const totalAmount = orders.reduce((total, product) => {
      const quantity = Number(product.quantity) || 1;
  
      // Handle isWeightBased services
      if (product.isWeightBased) {
        if (seenWeightBasedServices.has(product.serviceId)) {
          return total; // Already calculated for this service, skip
        }
  
        seenWeightBasedServices.add(product.serviceId);
        const weight = Number(product.totalWeight) || 1;
        const weightBasedTotal = product.price * weight;
  
        // Add weight-based price only once per service
        return total + weightBasedTotal;
      }
  
      // Non-weight-based service
      const addonTotal = product.serviceAddOns?.reduce(
        (sum: number, addon: any) => sum + addon.price * quantity,
        0
      ) || 0;
  
      return total + product.price * quantity + addonTotal;
    }, 0);
  
    setTotalAmount(totalAmount);
    return totalAmount;
  };
  

  const groupedOrders = orders.reduce((acc: any, order: any) => {
    const existingProduct = acc.find(
      (item: any) => item.productId === order.productId
    );

    if (existingProduct) {
      if (!existingProduct.services.includes(order.serviceName)) {
        existingProduct.services.push(order.serviceName);
        existingProduct.serviceIds.push(order.serviceId);
      }

      // Add addons only if they are unique
      order.serviceAddOns?.forEach((addon: any) => {
        if (
          !existingProduct.serviceAddOns.some(
            (a: any) => a.addOnId === addon.addOnId
          )
        ) {
          existingProduct.serviceAddOns.push(addon);
        }
      });

      // existingProduct.quantity += Number(order.quantity);
      const quantityOrWeight = order.isWeightBased
        ? Number(order.totalWeight || 1)
        : Number(order.quantity || 1);

      existingProduct.price += order.price * quantityOrWeight;
    } else {
      acc.push({
        productId: order.productId,
        productName: order.productName,
        productType: order.productType,
        services: [order.serviceName],
        serviceIds: [order.serviceId],
        serviceAddOns: order.serviceAddOns || [],
        quantity: Number(order.quantity),
        // weight: order.weight || "N/A",
        price: order.price * (order.isWeightBased ? Number(order.totalWeight || 1) : Number(order.quantity || 1)),
        isWeightBased: order.isWeightBased // Keep track for sorting
      });
    }

    return acc;
  }, []);

  // **Sort orders by isWeightBased (true first, false later)**
  groupedOrders.sort((a: any, b: any) => {
    return Number(b.isWeightBased) - Number(a.isWeightBased);
  });

  return (
    <TableContainer
      component={Paper}
      sx={{
        mt: 3,
        borderRadius: 0,
        boxShadow: 0,
        maxHeight: 400,
        overflow: "auto"
      }}
    >
      <Typography variant="h6" sx={{ p: 2, fontWeight: "bold" }}>
        Order Summary
      </Typography>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Name
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Type
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Service
            </TableCell>
            {/* <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Weight
            </TableCell> */}
            <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Quantity
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", fontSize: "13px" }}>
              Price
            </TableCell>
            <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
              Action
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {loading ? (
            // Show Skeleton Loader when data is loading
            [...Array(5)].map((_, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Skeleton variant="text" width={100} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={150} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={50} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={50} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={80} height={20} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
              </TableRow>
            ))
          ) : groupedOrders.length > 0 ? (
            // Show Actual Data when loaded
            groupedOrders.map((order: any) => (
              <TableRow key={order.productId}>
                <TableCell sx={{ fontSize: "12px" }}>
                  {order.productName}
                </TableCell>

                <TableCell sx={{ fontSize: "12px" }}>
                  {order.productType}
                </TableCell>
                <TableCell sx={{ fontSize: "10px", maxWidth: "200px" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "block",
                      maxWidth: "100%"
                    }}
                    title={order.services.join(", ")}
                  >
                    {order.services.join(", ")}
                  </Typography>
                  {order.serviceAddOns.length > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: "gray", display: "block" }}
                    >
                      Add-ons:{" "}
                      {order.serviceAddOns
                        .map((a: any) => a.addOnName)
                        .join(", ")}
                    </Typography>
                  )}
                </TableCell>
                {/* <TableCell sx={{ fontSize: "12px" }}>{order.weight}</TableCell> */}
                <TableCell sx={{ fontSize: "12px" }}>
                  {order.quantity}
                </TableCell>
                <TableCell sx={{ fontSize: "12px" }}>₹{order.price}</TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <WSIcons.Delete
                    color="error"
                    onClick={() => handleDelete(order.productId)}
                    sx={{ cursor: "pointer" }}
                  />
                </TableCell>
              </TableRow>
            ))
          ) : (
            // Show No Data UI when orders are empty
            <TableRow>
              <TableCell colSpan={6}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 150,
                    border: "1px dashed #ccc",
                    borderRadius: 2,
                    color: "#888",
                    mt: 2,
                    p: 2
                  }}
                >
                  <WSIcons.NoShoppingCartIcon />
                  <Typography variant="body1">No orders added yet</Typography>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Footer for Total & Date/Time */}
      {!loading && (
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f5f5f5",
            borderTop: "1px solid #ddd"
          }}
        >
          <Typography variant="body1">
            <b>Total Amount:</b> ₹{calculateTotal().toFixed(2)}
          </Typography>
          {weight > 0 && orders.length > 0 ? (
            <Typography variant="body2" sx={{ color: "gray" }}>
              Total Weight: {weight} KG
            </Typography>
          ) : (
            ""
          )}
        </Box>
      )}
    </TableContainer>
  );
};

export default OrderSummary;
